import { useEffect, useMemo, useState } from 'react';
import { ActionIcon, Grid, Select, Tooltip } from '@mantine/core';
import { DatePickerInput, DateValue } from '@mantine/dates';
import { IconFileSpreadsheet } from '@tabler/icons-react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import CatalogContainer from '../../../components/CatalogContainer/CatalogContainer';
import CatalogListHeader from '../../../components/CatalogListHeader/CatalogListHeader';
import useDownloadExcel from '../../../hooks/useDownloadExcel/useDownloadExcel';
import { getInitialReportRange, getRangeDate, getReportExclusiveEnd, getReportTimezone } from '../datePickerHelper';
import { getCashierBoxesByStore } from '../../../services/store.service';
import { exportCashierBoxMovementsReport } from '../../../services/reports.service';
import { useGetCashierBoxMovementsReportData } from '../../../hooks/reports/queries';
import { MRT_PaginationState } from 'mantine-react-table';
import TableView from './TableView';

const CashierBoxMovementsReport = () => {
  const { t } = useTranslation(['ReportsPage', 'FieldName', 'CommonButtons']);
  const location = useLocation();
  const { downloadExcelV2, loadingExport } = useDownloadExcel();

  const [dateRangePicker, setDateRange] = useState<[DateValue, DateValue]>(() => getInitialReportRange(location.search));
  const [appliedRange, setAppliedRange] = useState(dateRangePicker);
  const updateDateRange = (value: [DateValue, DateValue]) => {
    setDateRange(value);
    if (value[0] && value[1]) {
      setAppliedRange(value);
      setPagination((current) => ({ ...current, pageIndex: 0 }));
    }
  };
  const [selectedCashierBox, setSelectedCashierBox] = useState<string | null>(null);
  const limitPerReq = 10;
  const searchParams = new URLSearchParams(location.search);
  const searchParamStoreId = searchParams.get('storeId') || '';
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: limitPerReq
  });

  const timezone = getReportTimezone(location.search);
  const { fromDate, toDate } = getRangeDate(appliedRange, 'day', timezone, getReportExclusiveEnd(location.search));

  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [fromDate, toDate, selectedCashierBox]);

  const { data: cashierBoxes = [], isLoading: isLoadingCashierBoxes } = useQuery({
    enabled: !!searchParamStoreId,
    queryKey: ['cashierBoxes', searchParamStoreId],
    queryFn: async () => getCashierBoxesByStore(searchParamStoreId),
  });

  const cashierBoxOptions = useMemo(() => {
    return (cashierBoxes || []).map((cb: any) => ({
      value: cb._id,
      label: cb.name
    }));
  }, [cashierBoxes]);

  const {
    data,
    isLoading,
    isError
  } = useGetCashierBoxMovementsReportData(searchParamStoreId, {
    cashierBoxId: selectedCashierBox || undefined,
    fromDate,
    toDate,
    timezone: timezone,
    pageNum: pagination.pageIndex,
    limit: limitPerReq
  });

  const tableInfo = useMemo(() => {
    return data?.pages?.reduce((list: any[], { graphInfo }: { graphInfo: any[] }) => {
      list.push(...graphInfo);
      return list;
    }, [] as any[]);
  }, [data]);

  const footerInfo = useMemo(() => {
    return {
      totalAmountIn: data?.pages?.[0]?.totalAmountIn || 0,
      totalAmountOut: data?.pages?.[0]?.totalAmountOut || 0,
    };
  }, [data?.pages]);

  const totalRowsCount = useMemo(() => data?.pages?.[0]?.totalRows || -1, [data]);
  const pages = useMemo(() => Math.ceil(totalRowsCount / limitPerReq) || -1, [totalRowsCount]);

  return (
    <CatalogContainer>
      <CatalogListHeader
        title={t('ReportsPage:cashierBoxMovementsReport.title')}
        showCreateButton={false}
      />
      <Grid>
        <Grid.Col span={{ xs: 12, md: 'auto' }}>
          <DatePickerInput
            clearable={false}
            type="range"
            label={t('FieldName:date')}
            placeholder={t('FieldName:date')}
            value={dateRangePicker}
            onChange={updateDateRange}
            allowSingleDateInRange
          />
        </Grid.Col>
        <Grid.Col span={{ xs: 12, md: 4 }}>
          <Select
            label={t('FieldName:cashierBox')}
            placeholder={t('FieldName:cashierBox')}
            data={cashierBoxOptions}
            value={selectedCashierBox}
            onChange={setSelectedCashierBox}
            clearable
            searchable
            nothingFoundMessage={isLoadingCashierBoxes ? t('CommonButtons:loading') : t('CommonButtons:noData')}
          />
        </Grid.Col>
        <Grid.Col span={{ xs: 12, md: 'content' }} style={{ alignSelf: 'flex-end' }}>
          <Tooltip label={t('CommonButtons:exportList')}>
            <ActionIcon
              disabled={loadingExport}
              onClick={() => {
                downloadExcelV2(async () => {
                  try {
                    return await exportCashierBoxMovementsReport(searchParamStoreId, {
                      cashierBoxId: selectedCashierBox || undefined,
                      fromDate,
                      toDate,
                      timezone: timezone
                    });
                  } catch (e) {
                    throw e;
                  }
                }, `ReporteMovimientos_${searchParamStoreId}`);
              }}
              variant="filled"
              color="#1D6F42"
              aria-label="Descargar_Listado_CSV">
              <IconFileSpreadsheet style={{ width: '70%', height: '70%' }} stroke={1.5} />
            </ActionIcon>
          </Tooltip>
        </Grid.Col>
        <Grid.Col span={12}>
          <TableView
            tableInfo={tableInfo as any[]}
            totalRowsCount={totalRowsCount}
            pages={pages}
            isError={isError}
            isLoading={isLoading}
            pagination={pagination}
            setPagination={setPagination}
            footerInfo={footerInfo}
          />
        </Grid.Col>
      </Grid>
    </CatalogContainer>
  );
};

export default CashierBoxMovementsReport;
